import { ContentEditingShell } from '@/components/content-editing-shell';
import { MediaLibrary, type CaseMediaSelection } from '@/components/media-library';
import { Button } from '@/components/ui/button';
import { ContentManagementMenu } from '@/components/content-management-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCase } from '@/hooks/use-cases';
import { api } from '@/lib/api';
import { localizeContentImages } from '@/lib/localize-content-images';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  translationBlocks,
  validTranslationCopy,
  type ContentLanguage,
  type FieldTranslationMetadata,
  type LocalizedText,
  type TranslationCopy,
} from '@fame/shared';

type Category = 'incentive' | 'event' | 'luxury';
type Status = 'draft' | 'in_review' | 'changes_requested' | 'published';
type TiptapDocument = Record<string, unknown>;

const emptyDocument: TiptapDocument = { type: 'doc', content: [] };
const emptyLocalizedText: LocalizedText = { nl: '', fr: '', en: '' };
const statusLabels: Record<Status, string> = {
  draft: 'Concept',
  in_review: 'Ter review',
  changes_requested: 'Aanpassingen gevraagd',
  published: 'Gepubliceerd',
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function contentMediaIds(document: unknown): string[] {
  const ids = new Set<string>();
  const visit = (node: unknown) => {
    if (Array.isArray(node)) return node.forEach(visit);
    if (!node || typeof node !== 'object') return;
    const record = node as Record<string, unknown>;
    if (typeof record.src === 'string') {
      const match = record.src.match(/\/api\/public\/media\/([0-9a-f-]{36})(?:\?|$)/i);
      if (match) ids.add(match[1]);
    }
    Object.values(record).forEach(visit);
  };
  visit(document);
  return [...ids];
}

export function CaseEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: caseItem, isLoading } = useCase(id ?? '');
  const [title, setTitle] = useState<LocalizedText>(emptyLocalizedText);
  const [tagline, setTagline] = useState<LocalizedText>(emptyLocalizedText);
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugAvailability, setSlugAvailability] = useState<{ available: boolean; suggestedSlug: string }>();
  const [category, setCategory] = useState<Category>('incentive');
  const [content, setContent] = useState<TiptapDocument>(emptyDocument);
  const [sourceLanguage, setSourceLanguage] = useState<ContentLanguage>('nl');
  const [translations, setTranslations] = useState<Partial<Record<ContentLanguage, TranslationCopy>>>({});
  const [fieldMetadata, setFieldMetadata] = useState<FieldTranslationMetadata>({});
  const [subtitle, setSubtitle] = useState<LocalizedText>(emptyLocalizedText);
  const [location, setLocation] = useState<LocalizedText>(emptyLocalizedText);
  const [attendees, setAttendees] = useState<LocalizedText>(emptyLocalizedText);
  const [company, setCompany] = useState<LocalizedText>(emptyLocalizedText);
  const [duration, setDuration] = useState<LocalizedText>(emptyLocalizedText);
  const [seoDescription, setSeoDescription] = useState<LocalizedText>(emptyLocalizedText);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [media, setMedia] = useState<CaseMediaSelection[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hydratedCaseId = useRef<string | undefined>(undefined);
  const [hydratedId, setHydratedId] = useState<string>();

  useEffect(() => {
    if (!caseItem) return;
    if (hydratedCaseId.current === caseItem.id) return;
    hydratedCaseId.current = caseItem.id;
    setTitle({ ...emptyLocalizedText, ...caseItem.title });
    setTagline({ ...emptyLocalizedText, ...caseItem.tagline });
    setSlug(caseItem.slug);
    setSlugManuallyEdited(true);
    setCategory(caseItem.category);
    const savedContent = caseItem.content;
    if (savedContent && 'master' in savedContent) {
      // eslint-disable-next-line react/set-state-in-effect -- Hydrates the editable draft from the asynchronously loaded record.
      setContent(savedContent.master);
      setSourceLanguage(savedContent.sourceLanguage);
      setTranslations(
        Object.fromEntries(
          Object.entries(savedContent.translations ?? {}).map(([language, copy]) => [
            language,
            Object.fromEntries(
              Object.entries(copy ?? {}).filter(([blockId, entry]) =>
                validTranslationCopy(savedContent.master, { [blockId]: entry })
              )
            ),
          ])
        ) as Partial<Record<ContentLanguage, TranslationCopy>>
      );
      setFieldMetadata(savedContent.fieldMetadata ?? {});
    } else {
      setContent(savedContent?.nl ?? emptyDocument);
      setSourceLanguage('nl');
      setTranslations({});
      setFieldMetadata({});
    }
    setSubtitle({ ...emptyLocalizedText, ...caseItem.subtitle });
    setLocation({ ...emptyLocalizedText, ...caseItem.location });
    setAttendees({ ...emptyLocalizedText, ...caseItem.attendees });
    setCompany({ ...emptyLocalizedText, ...caseItem.company });
    setDuration({ ...emptyLocalizedText, ...caseItem.duration });
    setSeoDescription({ ...emptyLocalizedText, ...caseItem.seoDescription });
    setReviewNotes(caseItem.reviewNotes ?? '');
    setIsFeatured(caseItem.isFeatured);
    setSortOrder(caseItem.sortOrder);
    setMedia(
      caseItem.media.flatMap(({ mediaAssetId, role, sortOrder: mediaSortOrder, cropX }) =>
        role === 'hero' || role === 'gallery'
          ? [{ mediaAssetId, role, sortOrder: mediaSortOrder, cropX: cropX ?? 50 }]
          : []
      )
    );
    setHydratedId(caseItem.id);
  }, [caseItem]);

  useEffect(() => {
    const normalizedSlug = slugify(slug);
    if (!normalizedSlug) {
      // eslint-disable-next-line react/set-state-in-effect -- Clearing the slug must also clear the previously fetched availability.
      setSlugAvailability(undefined);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const result = await api.get<{ available: boolean; suggestedSlug: string }>(
          `/cases/slug-availability?slug=${encodeURIComponent(normalizedSlug)}${id ? `&excludeId=${id}` : ''}`
        );
        if (cancelled) return;
        setSlugAvailability(result);
        if (isNew && !slugManuallyEdited && result.suggestedSlug !== normalizedSlug) setSlug(result.suggestedSlug);
      } catch {
        if (!cancelled) setSlugAvailability(undefined);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [id, isNew, slug, slugManuallyEdited]);

  const save = async (status: Status) => {
    if (!title[sourceLanguage]?.trim() || !slug.trim()) {
      toast.error('Titel en slug zijn verplicht.');
      return;
    }

    setSaving(true);
    try {
      const masterBlockTypes = new Map(translationBlocks(content).map(block => [block.id, block.block.type]));
      const prunedTranslations = Object.fromEntries(
        Object.entries(translations)
          .filter(([language]) => language !== sourceLanguage)
          .map(([language, copy]) => [
            language,
            Object.fromEntries(
              Object.entries(copy ?? {}).filter(([id, entry]) => entry && masterBlockTypes.get(id) === entry.block.type)
            ),
          ])
      ) as Partial<Record<ContentLanguage, TranslationCopy>>;
      const localizedContent = await localizeContentImages({ master: content, translations: prunedTranslations });
      setContent(localizedContent.master);
      setTranslations(localizedContent.translations);
      const data = {
        category,
        slug: slugify(slug),
        title,
        tagline: Object.values(tagline).some(value => value.trim()) ? tagline : undefined,
        subtitle: Object.values(subtitle).some(value => value.trim()) ? subtitle : undefined,
        content: {
          master: localizedContent.master,
          sourceLanguage,
          translations: localizedContent.translations,
          fieldMetadata,
        },
        location: Object.values(location).some(value => value.trim()) ? location : undefined,
        attendees: Object.values(attendees).some(value => value.trim()) ? attendees : undefined,
        company: Object.values(company).some(value => value.trim()) ? company : undefined,
        duration: Object.values(duration).some(value => value.trim()) ? duration : undefined,
        status,
        isFeatured,
        sortOrder: Number.isFinite(sortOrder) && sortOrder >= 0 ? sortOrder : 0,
        seoDescription: Object.values(seoDescription).some(value => value.trim()) ? seoDescription : undefined,
        reviewNotes: reviewNotes.trim() || null,
        media: [
          ...media,
          ...contentMediaIds(localizedContent.master).map((mediaAssetId, index) => ({
            mediaAssetId,
            role: 'content' as const,
            sortOrder: index,
          })),
        ],
      };
      if (isNew) {
        await api.post('/cases', data);
        toast.success('Case aangemaakt.');
      } else {
        await api.put(`/cases/${id}`, data);
        toast.success('Case opgeslagen.');
      }
      // Do not briefly render an old list entry while the overview refetches.
      // A saved Case should always be read from the server on return.
      queryClient.removeQueries({ queryKey: ['cases'] });
      navigate('/cases');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'De case kon niet worden opgeslagen.');
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/cases/${id}`);
      toast.success('Case verwijderd.');
      navigate('/cases');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Verwijderen is niet gelukt.');
      setDeleting(false);
    }
  };

  if (!isNew && (isLoading || (caseItem && hydratedId !== caseItem.id))) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[36rem] w-full" />
      </div>
    );
  }

  if (!isNew && !caseItem) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Case niet gevonden.</p>
        <Button asChild variant="link">
          <Link to="/cases">Terug naar cases</Link>
        </Button>
      </div>
    );
  }

  const fields = [
    {
      key: 'field:title',
      label: 'Titel',
      value: title,
      setValue: (nextTitle: LocalizedText) => {
        setTitle(nextTitle);
        if (isNew && !slugManuallyEdited && nextTitle[sourceLanguage] !== title[sourceLanguage])
          setSlug(slugify(nextTitle[sourceLanguage] ?? ''));
      },
    },
    { key: 'field:tagline', label: 'Hero-titel', value: tagline, setValue: setTagline },
    { key: 'field:subtitle', label: 'Ondertitel', value: subtitle, setValue: setSubtitle },
    ...(category !== 'luxury'
      ? [
          { key: 'field:location', label: 'Locatie', value: location, setValue: setLocation, halfWidth: true },
          {
            key: 'field:attendees',
            label: 'Aantal deelnemers',
            value: attendees,
            setValue: setAttendees,
            halfWidth: true,
          },
          { key: 'field:company', label: 'Bedrijf', value: company, setValue: setCompany, halfWidth: true },
          { key: 'field:duration', label: 'Duur', value: duration, setValue: setDuration, halfWidth: true },
        ]
      : []),
    {
      key: 'field:seoDescription',
      label: 'SEO-beschrijving',
      value: seoDescription,
      setValue: setSeoDescription,
      multiline: true,
    },
  ];
  const draft = {
    category,
    slug: slugify(slug),
    title,
    tagline: Object.values(tagline).some(value => value.trim()) ? tagline : undefined,
    subtitle: Object.values(subtitle).some(value => value.trim()) ? subtitle : undefined,
    content: { master: content, sourceLanguage, translations, fieldMetadata },
    location: Object.values(location).some(value => value.trim()) ? location : undefined,
    attendees: Object.values(attendees).some(value => value.trim()) ? attendees : undefined,
    company: Object.values(company).some(value => value.trim()) ? company : undefined,
    duration: Object.values(duration).some(value => value.trim()) ? duration : undefined,
    status: caseItem?.status ?? 'draft',
    isFeatured,
    sortOrder: Number.isFinite(sortOrder) && sortOrder >= 0 ? sortOrder : 0,
    seoDescription: Object.values(seoDescription).some(value => value.trim()) ? seoDescription : undefined,
    reviewNotes: reviewNotes.trim() || null,
    media: [
      ...media,
      ...contentMediaIds(content).map((mediaAssetId, index) => ({
        mediaAssetId,
        role: 'content' as const,
        sortOrder: index,
      })),
    ],
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/cases">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{isNew ? 'Nieuwe case' : 'Case bewerken'}</h1>
          <p className="mt-1 text-muted-foreground">
            {isNew ? 'Maak een nieuwe websitecase aan.' : 'Werk de inhoud en publicatiestatus van deze case bij.'}
          </p>
        </div>
      </div>

      <ContentEditingShell
        key={id ?? 'new-case'}
        kind="case"
        isNew={isNew}
        sourceLanguage={sourceLanguage}
        onSourceLanguageChange={setSourceLanguage}
        content={content}
        onContentChange={setContent}
        translations={translations}
        onTranslationsChange={setTranslations}
        fieldMetadata={fieldMetadata}
        onFieldMetadataChange={setFieldMetadata}
        fields={fields}
        draft={draft}
        guardDisabled={saving || deleting}
        settings={
          <div className="grid gap-5">
            <label className="space-y-2">
              <span className="font-medium">Type</span>
              <select
                value={category}
                onChange={event => setCategory(event.target.value as Category)}
                className="flex h-10 w-full rounded-md border bg-background px-3"
              >
                <option value="incentive">Incentive</option>
                <option value="event">Event</option>
                <option value="luxury">Luxury Travel</option>
              </select>
            </label>
            <label className="space-y-2">
              <span className="font-medium">Slug</span>
              <Input
                value={slug}
                onChange={event => {
                  setSlug(event.target.value);
                  setSlugManuallyEdited(true);
                }}
              />
              <span
                className={`block text-sm ${slugAvailability?.available ? 'text-emerald-700' : slugAvailability ? 'text-amber-700' : 'text-muted-foreground'}`}
              >
                {slugAvailability?.available
                  ? `/${slugify(slug)} is beschikbaar`
                  : slugAvailability
                    ? `/${slugify(slug)} is al in gebruik; voorstel: /${slugAvailability.suggestedSlug}`
                    : `/${slugify(slug)}`}
              </span>
            </label>
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="flex items-center justify-between gap-3 rounded-md border p-3">
                <span>
                  <span className="block font-medium">Uitgelichte case</span>
                  <span className="text-sm text-muted-foreground">Toon deze case in een uitgelicht overzicht.</span>
                </span>
                <Switch checked={isFeatured} onCheckedChange={setIsFeatured} />
              </label>
              <label className="space-y-2">
                <span className="font-medium">Volgorde</span>
                <Input
                  type="number"
                  min="0"
                  value={sortOrder}
                  onChange={event => {
                    const next = Number(event.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <MediaLibrary value={media} onChange={setMedia} />
            <div className="space-y-3 rounded-md border p-4">
              <div>
                <p className="font-medium">Redactionele review</p>
                <p className="text-sm text-muted-foreground">
                  Huidige status: {isNew ? 'Nieuw' : statusLabels[caseItem?.status ?? 'draft']}. Voeg bij feedback een
                  notitie voor de schrijver toe.
                </p>
              </div>
              <label className="space-y-2">
                <span className="text-sm font-medium">Reviewnotities</span>
                <Textarea value={reviewNotes} onChange={event => setReviewNotes(event.target.value)} maxLength={4000} />
              </label>
            </div>
          </div>
        }
        actions={
          <>
            <Button type="button" onClick={() => save(caseItem?.status ?? 'draft')} disabled={saving || deleting}>
              {saving ? 'Opslaan…' : isNew ? 'Opslaan als concept' : 'Opslaan'}
            </Button>
            {(!caseItem || caseItem.status === 'draft' || caseItem.status === 'changes_requested') && (
              <Button type="button" variant="outline" onClick={() => save('in_review')} disabled={saving || deleting}>
                Ter review indienen
              </Button>
            )}
            {caseItem?.status === 'in_review' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => save('changes_requested')}
                  disabled={saving || deleting}
                >
                  Aanpassingen vragen
                </Button>
                <Button type="button" variant="outline" onClick={() => save('published')} disabled={saving || deleting}>
                  Publiceren
                </Button>
              </>
            )}
            {!isNew && (
              <ContentManagementMenu
                kind="case"
                disabled={saving || deleting}
                onRemove={remove}
                onWithdraw={
                  caseItem?.status === 'published'
                    ? () => {
                        void save('draft');
                      }
                    : undefined
                }
              />
            )}
          </>
        }
      />
    </div>
  );
}
